import {
  cleanMessageTemplates,
  CONTENT_GENERATION,
  DEFAULT_NEW_CAMPAIGN_CONTENT_GENERATION,
  getContentGeneration,
  validateMessageTemplates,
} from "@/features/campaigns/utils/stepContentMode";
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";
import {
  useCreateCampaign,
  useUpdateCampaign,
  useGetCampaignById,
  useAddCampaignAsset,
  useDeleteCampaignAsset,
  useUpdateCampaignIntegrations,
} from "@/features/campaigns/hooks/useCampaign";
import {
  industryLevelTriggers as industryLevelTriggerOptions,
  triggers as companyLevelTriggerOptions,
} from "@/shared/utils/utils";
import { normalizeCompanySizeList } from "@/shared/utils/onboardingConfig";
import { showError } from "@/shared/utils/toast";
import { campaignFormSchema } from "@/schema/OnBoarding";
import { NEW_CAMPAIGN_WIZARD_STEPS } from "./newCampaignModalConfig";
import {
  collectRHFErrorMessages,
  getZodErrorMessages,
} from "./wizardValidationUtils";

const splitLegacyTriggers = (legacy = []) => {
  const values = Array.isArray(legacy) ? legacy : [];
  const companySet = new Set(companyLevelTriggerOptions);
  const industrySet = new Set(industryLevelTriggerOptions);
  const companyLevelSalesTrigger = values.filter((v) => companySet.has(v));
  const industryLevelTrigger = values.filter((v) => industrySet.has(v));
  return { companyLevelSalesTrigger, industryLevelTrigger };
};

const TOTAL_WIZARD_STEPS = NEW_CAMPAIGN_WIZARD_STEPS.length;
const EMPTY_INTEGRATIONS = {
  emailChannelAccountId: "",
  linkedinChannelAccountId: "",
  whatsappChannelAccountId: "",
};

export function useNewCampaignWizard({ isOpen, onClose, campaignId = null }) {
  const isEditMode = !!campaignId;
  const [wizardStep, setWizardStep] = useState(0);
  const [formData, setFormData] = useState({
    campaignName: "",
    selectedServices: [],
    integrations: EMPTY_INTEGRATIONS,
  });
  const [pitchData, setPitchData] = useState(null);
  const [audienceData, setAudienceData] = useState(null);
  const [messageStyle, setMessageStyle] = useState([]);
  const [contentGeneration, setContentGeneration] = useState(DEFAULT_NEW_CAMPAIGN_CONTENT_GENERATION);
  const [messageTemplates, setMessageTemplates] = useState({});
  const [companyAssets, setCompanyAssets] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    pitchError: null,
    audienceError: null,
    messageStyleError: null,
  });
  const [topFieldErrors, setTopFieldErrors] = useState([]);
  const [isSubmittingCampaign, setIsSubmittingCampaign] = useState(false);

  const createCampaignMutation = useCreateCampaign();
  const updateCampaignMutation = useUpdateCampaign(campaignId);
  const updateIntegrationsMutation = useUpdateCampaignIntegrations(campaignId);
  const { mutateAsync: addAsset } = useAddCampaignAsset();
  const { mutateAsync: deleteAsset } = useDeleteCampaignAsset();
  const { data: campaignData, isLoading: isLoadingCampaign } =
    useGetCampaignById(campaignId, isEditMode && isOpen);

  const pitchPayloadCacheRef = useRef(null);
  /** Resolves when Step 1 pitch asset flush (uploads/deletes) finishes; submit awaits this. */
  const pitchFlushPromiseRef = useRef(null);
  const pitchSubmitHandlerRef = useRef(null);
  const audienceSubmitHandlerRef = useRef(null);
  const formValidationRef = useRef(null);
// created campiagn id
  const pendingCreatedCampaignIdRef = useRef(null);

  const stateRef = useRef({
    formData,
    pitchData,
    audienceData,
    messageStyle,
    companyAssets,
  });
  stateRef.current = { formData, pitchData, audienceData, messageStyle, companyAssets };

  const baselineStrRef = useRef(null);
  const baselineCapturedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      pitchPayloadCacheRef.current = null;
      pendingCreatedCampaignIdRef.current = null;
      setWizardStep(0);
      setFormData({
        campaignName: "",
        selectedServices: [],
        integrations: EMPTY_INTEGRATIONS,
      });
      setPitchData(null);
      setAudienceData(null);
      setMessageStyle([]);
      setCompanyAssets([]);
      setValidationErrors({
        pitchError: null,
        audienceError: null,
        messageStyleError: null,
      });
      setTopFieldErrors([]);
      setIsSubmittingCampaign(false);
      pitchFlushPromiseRef.current = null;
      return;
    }

    if (isEditMode && campaignData) {
      setFormData({
        campaignName: campaignData?.name || "",
        selectedServices: campaignData?.services || [],
      });

      if (campaignData?.define_pitch || campaignData?.assets) {
        const assets =
          campaignData.assets || campaignData.define_pitch?.companyAssets || [];
        const normalized = Array.isArray(assets)
          ? assets.map((a) =>
              typeof a === "object" && a?.id
                ? {
                    ...a,
                    file_name: a.file_name || a.filename || a.name,
                    context: a.context ?? "",
                  }
                : a,
            )
          : [];
        setPitchData({
          painPointsSolved: campaignData.define_pitch?.painPoints || "",
          valueProposition: campaignData.define_pitch?.valueProposition || "",
          ...(function getTriggers() {
            const companyLevelSalesTrigger =
              campaignData.define_pitch?.companyLevelSalesTrigger;
            const industryLevelTrigger =
              campaignData.define_pitch?.industryLevelTrigger;
            if (
              Array.isArray(companyLevelSalesTrigger) ||
              Array.isArray(industryLevelTrigger)
            ) {
              return {
                companyLevelSalesTrigger: companyLevelSalesTrigger || [],
                industryLevelTrigger: industryLevelTrigger || [],
              };
            }
            return splitLegacyTriggers(
              campaignData.define_pitch?.salesTrigger || [],
            );
          })(),
          signalTriggerConfig:
            campaignData.define_pitch?.signalTriggerConfig ||
            campaignData.define_pitch?.signal_trigger_config ||
            null,
          companyAssets: normalized,
          companyAssetsDetails:
            campaignData.define_pitch?.companyAssetsDetails || [],
        });
        setCompanyAssets(normalized);
      } else {
        setPitchData(null);
        setCompanyAssets([]);
      }

      if (campaignData?.target_audience) {
        setAudienceData({
          companySize: normalizeCompanySizeList(
            campaignData.target_audience.companySize,
          ),
          targetIndustries:
            campaignData.target_audience.targetIndutries ||
            campaignData.target_audience.targetIndustries ||
            [],
          roles: campaignData.target_audience.roles || [],
          businessLocations:
            campaignData.target_audience.businessLocation || [],
          idealCustomer: campaignData.target_audience.campaignDescription || "",
        });
      } else {
        setAudienceData(null);
      }

      const storedStyles =
        campaignData?.messageStyle ??
        campaignData?.message_style ??
        campaignData?.target_audience?.messageStyle ??
        [];
      setMessageStyle(
        Array.isArray(storedStyles) ? storedStyles.filter(Boolean) : [],
      );
      setContentGeneration(getContentGeneration(campaignData));
      setMessageTemplates(campaignData?.message_templates || {});
    } else if (!isEditMode) {
      setWizardStep(0);
      setContentGeneration(DEFAULT_NEW_CAMPAIGN_CONTENT_GENERATION);
      setMessageTemplates({});
      setFormData({
        campaignName: "",
        selectedServices: [],
        integrations: EMPTY_INTEGRATIONS,
      });
      setPitchData(null);
      setAudienceData(null);
      setMessageStyle([]);
      setCompanyAssets([]);
      setValidationErrors({
        pitchError: null,
        audienceError: null,
        messageStyleError: null,
      });
      setTopFieldErrors([]);
      setIsSubmittingCampaign(false);
      pitchFlushPromiseRef.current = null;
    }
  }, [isOpen, isEditMode, campaignData]);

  useEffect(() => {
    if (!isOpen) {
      baselineCapturedRef.current = false;
      baselineStrRef.current = null;
      return;
    }
    baselineCapturedRef.current = false;
    baselineStrRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode && isLoadingCampaign) return;
    if (baselineCapturedRef.current) return;
    const id = requestAnimationFrame(() => {
      baselineStrRef.current = JSON.stringify(stateRef.current);
      baselineCapturedRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, isEditMode, isLoadingCampaign, campaignData, campaignId]);

  const clearTopFieldErrors = useCallback(() => {
    setTopFieldErrors([]);
  }, []);

  const handleMessageStyleChange = useCallback((styles) => {
    setMessageStyle(Array.isArray(styles) ? styles : []);
    if (styles?.length) {
      setValidationErrors((prev) => ({ ...prev, messageStyleError: null }));
      setTopFieldErrors([]);
    }
  }, []);

  const isDirtyModal = useMemo(() => {
    if (!isOpen || baselineStrRef.current == null) return false;
    const snapshot = { formData, pitchData, audienceData, messageStyle, companyAssets };
    return JSON.stringify(snapshot) !== baselineStrRef.current;
  }, [isOpen, formData, pitchData, audienceData, messageStyle, companyAssets]);

  useUnsavedChangesWarning(isDirtyModal);

  const registerPitchSubmitHandler = useCallback((handler) => {
    pitchSubmitHandlerRef.current = handler;
  }, []);

  const registerAudienceSubmitHandler = useCallback((handler) => {
    audienceSubmitHandlerRef.current = handler;
  }, []);

  const handleFormDataChange = useCallback((data) => {
    setFormData(data);
    setTopFieldErrors([]);
  }, []);

  const handlePitchDataChange = useCallback((data) => {
    setPitchData((prev) => ({ ...(prev || {}), ...data }));
    if (data) {
      setValidationErrors((prev) => ({ ...prev, pitchError: null }));
      setTopFieldErrors([]);
    }
  }, []);

  const handleAudienceDataChange = useCallback((data) => {
    setAudienceData(data);
    if (data) {
      setValidationErrors((prev) => ({ ...prev, audienceError: null }));
      setTopFieldErrors([]);
    }
  }, []);

  const handleStep0Continue = useCallback(async () => {
    pitchPayloadCacheRef.current = null;
    setValidationErrors({ pitchError: null, audienceError: null });
    const formValid = await formValidationRef.current?.trigger?.();
    if (!formValid) {
      const rhfErrors = collectRHFErrorMessages(
        formValidationRef.current?.errors,
      );
      setTopFieldErrors(
        rhfErrors.length
          ? rhfErrors
          : getZodErrorMessages(campaignFormSchema, formData),
      );
      return;
    }
    if (!formData.campaignName?.trim()) return;
    if (!formData.selectedServices || formData.selectedServices.length === 0) {
      return;
    }
    setTopFieldErrors([]);
    setWizardStep(1);
  }, [formData]);

  /** Apply pendingDeletes + newFiles uploads; returns merged pitch payload and normalized rows for UI. */
  const flushPitchAssetsFromResult = useCallback(
    async (pitchDataResult, companyAssetsSnapshot) => {
      const pendingDeletes = pitchDataResult?.pendingDeletes ?? [];
      const newFiles = pitchDataResult?.newFiles ?? [];
      const keptIds = [...(pitchDataResult?.companyAssetIds ?? [])];
      const keptContexts = [...(pitchDataResult?.companyAssetsDetails ?? [])];
      const newAssetDetails = [...(pitchDataResult?.newAssetDetails ?? [])];

      for (const assetId of pendingDeletes) {
        await deleteAsset({ assetId, suppressSuccessToast: true });
      }

      const newIds = [];
      const newContexts = [];
      const uploadedRows = [];

      for (const item of newFiles) {
        const context = item?.context ?? "";
        const fileObj = item?.file?.file || item?.file;
        if (fileObj instanceof File) {
          const res = await addAsset({
            assetData: fileObj,
            description: JSON.stringify(context) || "",
          });
          const assetData = res?.data || res;
          if (assetData?.id) {
            newIds.push(assetData.id);
            newContexts.push(context);
            uploadedRows.push({
              id: assetData.id,
              file_name:
                assetData.file_name ||
                assetData.filename ||
                fileObj.name ||
                "Uploaded file",
              context,
            });
          }
        }
      }

      const companyAssetIds = [...keptIds, ...newIds];
      const companyAssetsDetails = [
        ...keptContexts,
        ...newContexts,
        ...newAssetDetails,
      ];

      const merged = {
        ...pitchDataResult,
        companyAssetIds,
        companyAssetsDetails,
        pendingDeletes: [],
        newFiles: [],
        newAssetDetails: [],
      };

      const snapshot = Array.isArray(companyAssetsSnapshot)
        ? companyAssetsSnapshot
        : [];
      const keptRows = keptIds.map((id, idx) => {
        const prev = snapshot.find((a) => a && String(a.id) === String(id));
        const ctx = keptContexts[idx] ?? prev?.context ?? "";
        return {
          id,
          file_name:
            prev?.file_name ||
            prev?.filename ||
            prev?.name ||
            "Uploaded file",
          context: ctx,
        };
      });

      const unattachedRows = newAssetDetails.map((context, idx) => ({
        fileId: `local-persisted-${Date.now()}-${idx}`,
        name: "",
        file: null,
        context,
      }));

      console.log("[useNewCampaignWizard] merged pitch assets", {
        attachedRows: companyAssetIds.length,
        unattachedRows: unattachedRows.length,
        detailRows: companyAssetsDetails.length,
      });

      return {
        merged,
        companyAssetsNormalized: [
          ...keptRows,
          ...uploadedRows,
          ...unattachedRows,
        ],
      };
    },
    [addAsset, deleteAsset],
  );

  const handleStep1Continue = useCallback(async () => {
    if (!pitchSubmitHandlerRef.current) {
      setValidationErrors((prev) => ({
        ...prev,
        pitchError: "Please complete your pitch",
      }));
      setTopFieldErrors(["Please complete your pitch"]);
      return;
    }
    const result = await pitchSubmitHandlerRef.current();
    if (!result?.success) {
      setTopFieldErrors(
        result?.fieldErrors?.length
          ? result.fieldErrors
          : ["Please fill all required fields in pitch"],
      );
      return;
    }

    pitchPayloadCacheRef.current = result.data ?? {};
    const snapshot = stateRef.current.companyAssets;

    const flushPromise = flushPitchAssetsFromResult(
      result.data ?? {},
      snapshot,
    )
      .then(({ merged, companyAssetsNormalized }) => {
        pitchPayloadCacheRef.current = merged;
        setCompanyAssets(companyAssetsNormalized);
        setPitchData((prev) => ({
          ...(prev || {}),
          painPointsSolved: merged.painPointsSolved,
          valueProposition: merged.valueProposition,
          companyLevelSalesTrigger: merged.companyLevelSalesTrigger,
          industryLevelTrigger: merged.industryLevelTrigger,
          signalTriggerConfig: merged.signalTriggerConfig,
          companyAssets: companyAssetsNormalized,
          companyAssetsDetails: merged.companyAssetsDetails,
        }));
        return merged;
      })
      .catch((err) => {
        console.error("Pitch asset upload failed:", err);
        showError(
          err?.message || "Could not upload pitch attachments. Try again.",
        );
        throw err;
      })
      .finally(() => {
        if (pitchFlushPromiseRef.current === flushPromise) {
          pitchFlushPromiseRef.current = null;
        }
      });

    pitchFlushPromiseRef.current = flushPromise;

    setValidationErrors((prev) => ({ ...prev, pitchError: null }));
    setTopFieldErrors([]);
    setWizardStep(2);
  }, [flushPitchAssetsFromResult]);

  const buildPitchFromState = useCallback(() => {
    if (!pitchData) {
      return {
        painPointsSolved: "",
        valueProposition: "",
        companyLevelSalesTrigger: [],
        industryLevelTrigger: [],
        signalTriggerConfig: null,
        companyAssetIds: [],
        companyAssetsDetails: [],
        pendingDeletes: [],
        newFiles: [],
        newAssetDetails: [],
      };
    }
    return {
      painPointsSolved: pitchData.painPointsSolved,
      valueProposition: pitchData.valueProposition,
      companyLevelSalesTrigger: pitchData.companyLevelSalesTrigger,
      industryLevelTrigger: pitchData.industryLevelTrigger,
      signalTriggerConfig: pitchData.signalTriggerConfig,
      companyAssetIds: (companyAssets || [])
        .filter((a) => a && typeof a === "object" && a.id)
        .map((a) => a.id),
      companyAssetsDetails: (companyAssets || [])
        .filter((a) => a && typeof a === "object" && a.id)
        .map((a) => a.context ?? ""),
      newAssetDetails: (companyAssets || [])
        .filter((a) => a && typeof a === "object" && !a.id && a.context)
        .map((a) => a.context),
      pendingDeletes: [],
      newFiles: [],
    };
  }, [pitchData, companyAssets]);

  const closeWizardAfterSave = useCallback(() => {
    pitchPayloadCacheRef.current = null;
    pitchFlushPromiseRef.current = null;
    const finalId = pendingCreatedCampaignIdRef.current || campaignId;
    pendingCreatedCampaignIdRef.current = null;
    setFormData({
      campaignName: "",
      selectedServices: [],
      integrations: EMPTY_INTEGRATIONS,
    });
    setPitchData(null);
    setAudienceData(null);
    setMessageStyle([]);
    setCompanyAssets([]);
    setWizardStep(0);
    onClose(finalId);
  }, [onClose, campaignId]);

  /** Validates, persists campaign; returns success without closing modal. */
  const persistCampaign = useCallback(async () => {
    setValidationErrors({
      pitchError: null,
      audienceError: null,
      messageStyleError: null,
    });
    setTopFieldErrors([]);

    if (!pitchData?.valueProposition?.trim()) {
      const pitchErrors = ["Please define your pitch"];
      setValidationErrors((prev) => ({
        ...prev,
        pitchError: pitchErrors[0],
      }));
      setTopFieldErrors(pitchErrors);
      setWizardStep(1);
      return false;
    }

    if (!audienceData) {
      const audienceErrors = ["Please define your target audience"];
      setValidationErrors((prev) => ({
        ...prev,
        audienceError: audienceErrors[0],
      }));
      setTopFieldErrors(audienceErrors);
      setWizardStep(2);
      return false;
    }

    if (!Array.isArray(messageStyle) || messageStyle.length === 0) {
      const styleErrors = ["Select at least one messaging style"];
      setValidationErrors((prev) => ({
        ...prev,
        messageStyleError: styleErrors[0],
      }));
      setTopFieldErrors(styleErrors);
      setWizardStep(3);
      return false;
    }

    // With AI on the Campaign messages section is hidden, so it must not block saving.
    const templateErrors = validateMessageTemplates(messageTemplates);
    if (contentGeneration === CONTENT_GENERATION.OFF && Object.keys(templateErrors).length) {
      setTopFieldErrors(["Fix the campaign messages before continuing"]);
      setWizardStep(3);
      return false;
    }

    setIsSubmittingCampaign(true);
    try {
      const pitchFlushWait = pitchFlushPromiseRef.current;
      if (pitchFlushWait) {
        try {
          await pitchFlushWait;
        } catch {
          setValidationErrors((prev) => ({
            ...prev,
            pitchError:
              "Pitch attachments failed to upload. Go back and try again.",
          }));
          setWizardStep(1);
          return false;
        }
      }

      let pitchDataResult = pitchPayloadCacheRef.current;
      if (!pitchDataResult) {
        let pitchResult = null;
        if (pitchSubmitHandlerRef.current) {
          pitchResult = await pitchSubmitHandlerRef.current();
          if (!pitchResult?.success) {
            const pitchErrors = pitchResult?.fieldErrors?.length
              ? pitchResult.fieldErrors
              : ["Please fill all required fields in pitch"];
            setValidationErrors((prev) => ({
              ...prev,
              pitchError: pitchErrors[0],
            }));
            setTopFieldErrors(pitchErrors);
            setWizardStep(1);
            return false;
          }
          pitchDataResult = pitchResult.data;
        }
      }

      if (
        !pitchDataResult ||
        (typeof pitchDataResult === "object" &&
          Object.keys(pitchDataResult).length === 0)
      ) {
        pitchDataResult = buildPitchFromState();
      }

      if (audienceSubmitHandlerRef.current) {
        const audienceResult = await audienceSubmitHandlerRef.current();
        if (!audienceResult?.success) {
          const audienceErrors = audienceResult?.fieldErrors?.length
            ? audienceResult.fieldErrors
            : ["Please fill all required fields in audience"];
          setValidationErrors((prev) => ({
            ...prev,
            audienceError: audienceErrors[0],
          }));
          setTopFieldErrors(audienceErrors);
          return false;
        }
      }

      let resolvedPitch = pitchDataResult;
      const pendingFlushDeletes = pitchDataResult?.pendingDeletes ?? [];
      const pendingFlushFiles = pitchDataResult?.newFiles ?? [];
      const pendingFlushDetails = pitchDataResult?.newAssetDetails ?? [];
      if (
        pendingFlushDeletes.length > 0 ||
        pendingFlushFiles.length > 0 ||
        pendingFlushDetails.length > 0
      ) {
        const { merged, companyAssetsNormalized } =
          await flushPitchAssetsFromResult(
            pitchDataResult,
            stateRef.current.companyAssets,
          );
        resolvedPitch = merged;
        pitchPayloadCacheRef.current = merged;
        setCompanyAssets(companyAssetsNormalized);
        setPitchData((prev) => ({
          ...(prev || {}),
          painPointsSolved: merged.painPointsSolved,
          valueProposition: merged.valueProposition,
          companyLevelSalesTrigger: merged.companyLevelSalesTrigger,
          industryLevelTrigger: merged.industryLevelTrigger,
          companyAssets: companyAssetsNormalized,
          companyAssetsDetails: merged.companyAssetsDetails,
        }));
      }

      const {
        painPointsSolved,
        valueProposition,
        companyLevelSalesTrigger,
        industryLevelTrigger,
        companyAssetIds = [],
        companyAssetsDetails = [],
      } = resolvedPitch;

      const campaignPayload = {
        campaignName: formData.campaignName.trim(),
        campaignService: formData.selectedServices,
        messageStyle,
        contentGeneration,
        // Hidden (AI on) channels that are half-written are dropped instead of failing the save.
        messageTemplates: cleanMessageTemplates(
          Object.fromEntries(
            Object.entries(messageTemplates || {}).filter(([key]) => !templateErrors[key]),
          ),
        ),
        definePitch: {
          painPoints: painPointsSolved || "",
          valueProposition: valueProposition || "",
          companyLevelSalesTrigger: companyLevelSalesTrigger || [],
          industryLevelTrigger: industryLevelTrigger || [],
          signalTriggerConfig: resolvedPitch.signalTriggerConfig || null,
          companyAssetIds,
          companyAssetsDetails,
        },
        targetAudience: {
          companySize: normalizeCompanySizeList(audienceData.companySize),
          targetIndutries: audienceData.targetIndustries || [],
          roles: audienceData.roles || [],
          businessLocation: audienceData.businessLocations || [],
          campaignDescription: audienceData.idealCustomer || "",
        },
        integrations: formData.integrations || EMPTY_INTEGRATIONS,
      };

      if (isEditMode && campaignId) {
        await updateCampaignMutation.mutateAsync({
          campaignId,
          campaignData: campaignPayload,
        });
      } else {
        const pendingId = pendingCreatedCampaignIdRef.current;
        if (pendingId) {
          await updateCampaignMutation.mutateAsync({
            campaignId: pendingId,
            campaignData: campaignPayload,
          });
          await updateIntegrationsMutation.mutateAsync(
            {
              campaignId: pendingId,
              payload: formData.integrations || EMPTY_INTEGRATIONS,
            },
          );
        } else {
          const created = await createCampaignMutation.mutateAsync(
            campaignPayload,
          );
          const row = created?.data ?? created;
          const newId = row?.id ?? created?.id;
          if (newId) pendingCreatedCampaignIdRef.current = String(newId);
        }
      }
      pitchPayloadCacheRef.current = null;
      pitchFlushPromiseRef.current = null;
      return true;
    } catch (error) {
      console.error("Error creating/updating campaign:", error);
      showError(
        error?.message ||
          "Could not save campaign. Please try again.",
      );
      return false;
    } finally {
      setIsSubmittingCampaign(false);
    }
  }, [
    pitchData,
    audienceData,
    messageStyle,
    contentGeneration,
    messageTemplates,
    formData,
    isEditMode,
    campaignId,
    buildPitchFromState,
    flushPitchAssetsFromResult,
    updateCampaignMutation,
    createCampaignMutation,
    updateIntegrationsMutation,
  ]);

  const handleAudienceContinueToMessaging = useCallback(async () => {
    setValidationErrors((prev) => ({ ...prev, audienceError: null }));
    if (!audienceSubmitHandlerRef.current) {
      const audienceErrors = ["Please complete your target audience"];
      setValidationErrors((prev) => ({
        ...prev,
        audienceError: audienceErrors[0],
      }));
      setTopFieldErrors(audienceErrors);
      return;
    }
    const audienceResult = await audienceSubmitHandlerRef.current();
    if (!audienceResult?.success) {
      const audienceErrors = audienceResult?.fieldErrors?.length
        ? audienceResult.fieldErrors
        : ["Please fill all required fields in audience"];
      setValidationErrors((prev) => ({
        ...prev,
        audienceError: audienceErrors[0],
      }));
      setTopFieldErrors(audienceErrors);
      return;
    }
    setTopFieldErrors([]);
    setWizardStep(3);
  }, []);

  const handleMessagingContinueToTraining = useCallback(async () => {
    const ok = await persistCampaign();
    if (ok) setWizardStep(4);
  }, [persistCampaign]);

  const handleSkipAiTrainingStep = useCallback(() => {
    closeWizardAfterSave();
  }, [closeWizardAfterSave]);

  const handleTrainerLinkClick = useCallback(() => {
    closeWizardAfterSave();
  }, [closeWizardAfterSave]);

  const progressValue =
    TOTAL_WIZARD_STEPS > 1
      ? Math.floor((wizardStep / (TOTAL_WIZARD_STEPS - 1)) * 100)
      : 0;

  const isSavingCampaign =
    isSubmittingCampaign ||
    (isEditMode
      ? updateCampaignMutation.isPending
      : createCampaignMutation.isPending || updateIntegrationsMutation.isPending);

  const currentStepMeta = NEW_CAMPAIGN_WIZARD_STEPS[wizardStep] ?? NEW_CAMPAIGN_WIZARD_STEPS[0];

  return {
    isEditMode,
    wizardStep,
    setWizardStep,
    formData,
    pitchData,
    audienceData,
    messageStyle,
    companyAssets,
    validationErrors,
    topFieldErrors,
    clearTopFieldErrors,
    handleMessageStyleChange,
    contentGeneration,
    setContentGeneration,
    messageTemplates,
    setMessageTemplates,
    formValidationRef,
    registerPitchSubmitHandler,
    registerAudienceSubmitHandler,
    handleFormDataChange,
    handlePitchDataChange,
    handleAudienceDataChange,
    handleStep0Continue,
    handleStep1Continue,
    handleAudienceContinueToMessaging,
    handleMessagingContinueToTraining,
    handleSkipAiTrainingStep,
    handleTrainerLinkClick,
    progressValue,
    isSavingCampaign,
    isLoadingCampaign,
    currentStepMeta,
    wizardSteps: NEW_CAMPAIGN_WIZARD_STEPS,
  };
}
